import { Component, OnInit, OnDestroy, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ApiError } from '../../core/models/auth.models';
import { environment } from '../../../environments/environment';
import { LanguageSwitcherComponent } from '../../shared/components/language-switcher/language-switcher.component';
import { AppStatusService } from '../../core/services/app-status.service';

/**
 * Composant de connexion avec animations et intégration API réelle
 * Architecture: Angular Standalone + PrimeNG + Reactive Forms
 * API: POST /api/v1/auth/login | POST /api/v1/auth/login-otp
 */
@Component({
  selector: 'app-login',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CheckboxModule,
    ProgressSpinnerModule,
    ToastModule,
    TranslateModule,
    LanguageSwitcherComponent,
  ],
  templateUrl: './login.component.html',
  animations: [
    // Animation de fondu global
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1 })),
      ]),
    ]),
    // Animation de slide up pour le contenu
    trigger('slideInUp', [
      transition(':enter', [
        style({ transform: 'translateY(20px)', opacity: 0 }),
        animate('400ms 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
    // Animation de slide pour les formulaires
    trigger('formSlide', [
      transition(':enter', [
        style({ transform: 'translateX(-20px)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
    ]),
    // Animation pour les champs
    trigger('fieldFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    // Animation pour les erreurs
    trigger('errorSlide', [
      transition(':enter', [
        style({ height: 0, opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
    // Animation pour indicateur offline
    trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateY(-100%)', opacity: 0 })),
      ]),
    ]),
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly appStatusService = inject(AppStatusService);
  private readonly destroy$ = new Subject<void>();

  loginForm!: FormGroup;
  phoneForm!: FormGroup;
  showPassword = false;
  isLoading = false;
  activeTab: 'email' | 'phone' = 'email';
  returnUrl: string | null = null;
  isOnline = navigator.onLine;
  appVersion = environment.appVersion || '1.0.0';

  get isMaintenanceMode(): boolean {
    return this.appStatusService.maintenanceMode;
  }
  private networkErrorAttempts = 0;

  ngOnInit(): void {
    // Vérifier le statut de connexion initial
    this.isOnline = navigator.onLine;

    // Écouter les changements de connexion
    window.addEventListener('online', () => { this.isOnline = true; });
    window.addEventListener('offline', () => { this.isOnline = false; });

    // Récupérer l'URL de retour depuis les query params
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.returnUrl = params['returnUrl'] || null;
    });

    // Initialiser les formulaires
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false],
    });

    this.phoneForm = this.fb.group({
      phone: ['', [Validators.required, this.phoneValidator]],
      rememberMe: [false],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Gérer le changement d'onglet avec animation
   */
  setActiveTab(tab: 'email' | 'phone'): void {
    if (this.activeTab === tab) return;

    this.activeTab = tab;

    // Annoncer le changement pour les lecteurs d'écran
    const tabName = tab === 'email'
      ? this.translate.instant('LOGIN.TAB_EMAIL')
      : this.translate.instant('LOGIN.TAB_PHONE');

    this.announce(`${tabName} ${this.translate.instant('COMMON.SELECTED')}`);
  }


  /**
   * Annoncer un message pour l'accessibilité
   */
  private announce(message: string): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }


  onSubmit(): void {
    if (this.isLoading) return;

    if (this.activeTab === 'email') {
      this.handleEmailLogin();
    } else {
      this.handlePhoneLogin();
    }
  }

  /**
   * Gérer le login avec email/password
   * Intégration API: POST /api/v1/auth/login
   * Avec feedback visuel et animation
   */
  private async handleEmailLogin(): Promise<void> {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched(this.loginForm);
      this.shakeForm();
      return;
    }

    // Vérifier la connexion - Le NetworkStatusService affiche déjà un toast
    if (!this.isOnline) {
      return;
    }

    this.isLoading = true;
    const credentials = this.loginForm.value;

    try {
      const response = await this.authService.login({
        email: credentials.email,
        password: credentials.password,
      });

      const isMobile = this.breakpointObserver.isMatched('(max-width: 768px)');

      // Navigation conditionnelle selon category et roles
      const targetRoute = this.authService.getDefaultRoute(isMobile);

      try {
        await this.router.navigate([targetRoute]);
        this.isLoading = false;
      } catch (navError) {
        console.error('❌ Navigation error:', navError);
        // En cas d'erreur de chargement du module, recharger la page
        // Cela permettra au Service Worker de mettre en cache les chunks manquants
        window.location.href = targetRoute;
      }
    } catch (error: any) {
      this.isLoading = false;
      console.error('❌ Login error:', error);
      this.handleApiError(error);
      this.shakeForm();
      this.handleNetworkErrorAttempts(error);
    }
  }

  /**
   * Animation de secousse pour feedback visuel d'erreur
   */
  private shakeForm(): void {
    const form = document.querySelector('.login-form');
    if (form) {
      form.classList.add('shake-animation');
      setTimeout(() => form.classList.remove('shake-animation'), 500);
    }
  }

  /**
   * Gérer le login avec téléphone/OTP
   * Intégration API: POST /api/v1/auth/login-otp
   * Avec feedback visuel et animation
   */
  private async handlePhoneLogin(): Promise<void> {
    if (this.phoneForm.invalid) {
      this.markFormGroupTouched(this.phoneForm);
      this.shakeForm();
      return;
    }

    // Vérifier la connexion - Le NetworkStatusService affiche déjà un toast
    if (!this.isOnline) {
      return;
    }

    this.isLoading = true;
    const phone = this.phoneForm.get('phone')?.value;

    try {
      const response = await this.authService.requestOtp({ emailOrTel: phone });

      // Extraire les données du wrapper API
      const otpData = response.data || response;
      const message = otpData.message || this.translate.instant('LOGIN.OTP_SENT_MESSAGE');

      // Success feedback
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('LOGIN.OTP_SENT_TITLE'),
        detail: message,
        life: 3000,
        icon: 'pi pi-mobile',
      });

      // Annoncer pour l'accessibilité
      this.announce(this.translate.instant('LOGIN.OTP_SENT_MESSAGE'));

      // Attendre légèrement pour feedback visuel
      await new Promise(resolve => setTimeout(resolve, 600));

      this.isLoading = false;
      this.router.navigate(['/auth/verification'], {
        queryParams: { phone, method: 'otp' },
      });
    } catch (error: any) {
      this.isLoading = false;
      this.handleApiError(error);
      this.shakeForm();
      this.handleNetworkErrorAttempts(error);
    }
  }

  /**
   * Gérer les erreurs API selon la structure NestJS
   * Avec messages contextuels et accessibilité
   */
  private handleApiError(error: ApiError): void {
    let errorMessage = this.translate.instant('LOGIN.ERROR_MESSAGE');
    let errorTitle = this.translate.instant('LOGIN.ERROR_TITLE');
    let errorIcon = 'pi pi-times-circle';

    // Erreur "bad credential" de l'API
    if (error.error === 'bad credential' || error.message === 'Invalid credentials') {
      errorMessage = this.translate.instant('LOGIN.INVALID_CREDENTIALS') || 'Identifiants invalides';
      errorIcon = 'pi pi-exclamation-triangle';
    }
    // Erreur de réseau
    else if (error.statusCode === 0 || error.statusCode === 500) {
      errorMessage = this.translate.instant('LOGIN.NETWORK_ERROR');
      errorTitle = this.translate.instant('COMMON.CONNECTION_ERROR');
      errorIcon = 'pi pi-wifi';
    }
    // Compte bloqué
    else if (error.statusCode === 423) {
      errorMessage = this.translate.instant('LOGIN.ACCOUNT_LOCKED');
      errorIcon = 'pi pi-lock';
    }
    // Trop de tentatives
    else if (error.statusCode === 429) {
      errorMessage = this.translate.instant('LOGIN.TOO_MANY_ATTEMPTS');
      errorIcon = 'pi pi-ban';
    }
    // Message de l'API
    else if (typeof error.message === 'string') {
      errorMessage = error.message;
    } else if (Array.isArray(error.message)) {
      errorMessage = error.message.join(', ');
    }

    // Afficher le toast avec l'icône appropriée
    this.messageService.add({
      severity: 'error',
      summary: errorTitle,
      detail: errorMessage,
      life: 5000,
      icon: errorIcon,
    });

    // Annoncer pour l'accessibilité
    this.announce(`${errorTitle}: ${errorMessage}`);

    // Log pour debugging (peut être retiré en production)
    console.error('Login Error:', {
      statusCode: error.statusCode,
      error: error.error,
      message: error.message,
      path: error.path,
    });
  }

  /**
   * Gérer le comptage des erreurs réseau et afficher un toast
   * spécifique après 3 tentatives échouées
   */
  private handleNetworkErrorAttempts(error: any): void {
    if (!this.isNetworkError(error)) {
      // Réinitialiser le compteur si ce n'est pas une erreur réseau
      this.networkErrorAttempts = 0;
      return;
    }

    this.networkErrorAttempts += 1;

    if (this.networkErrorAttempts >= 3) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMON.CONNECTION_ERROR'),
        detail: this.translate.instant('LOGIN.NETWORK_ERROR'),
        life: 6000,
        icon: 'pi pi-wifi',
      });

      // Annonce accessibilité
      this.announce(`${this.translate.instant('COMMON.CONNECTION_ERROR')}: ${this.translate.instant('LOGIN.NETWORK_ERROR')}`);

      // Réinitialiser après notification
      this.networkErrorAttempts = 0;
    }
  }

  /**
   * Déterminer si l'erreur correspond à un problème réseau
   */
  private isNetworkError(error: any): boolean {
    if (!error) return false;

    const statusCode = (error as any).statusCode ?? (error as any).status;
    const message = (error as any).message;

    if (statusCode === 0) {
      return true;
    }

    if (typeof message === 'string' && message.toLowerCase().includes('network error')) {
      return true;
    }

    return false;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private phoneValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    // Format téléphone camerounais: 6XXXXXXXX (9 chiffres)
    const phoneRegex = /^(\+237|237)?[6][0-9]{8}$/;
    const cleaned = control.value.replace(/\s/g, '');
    return phoneRegex.test(cleaned) ? null : { invalidPhone: true };
  }

  get email(): AbstractControl | null {
    return this.loginForm.get('email');
  }

  get password(): AbstractControl | null {
    return this.loginForm.get('password');
  }

  get phone(): AbstractControl | null {
    return this.phoneForm.get('phone');
  }
}
